<template>
  <nav 
    class="bg-white dark:bg-gray-900 shadow-sm sticky top-0 z-50" 
    role="navigation" 
    aria-label="Main navigation"
  >
    <div class="container-custom">
      <div class="flex justify-between items-center h-16">
        <!-- Logo/Brand -->
        <router-link 
          to="/" 
          class="flex items-center space-x-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg"
          aria-label="SecondChance.dev homepage"
        >
          <span class="text-2xl font-bold gradient-text">SecondChance</span>
        </router-link>

        <!-- Desktop Navigation -->
        <div class="hidden md:flex items-center space-x-6" role="menubar">
          <router-link
            v-for="link in navLinks"
            :key="link.name"
            :to="link.path"
            :class="[
              'nav-link',
              route.name === link.name ? 'active' : ''
            ]"
            :aria-current="route.name === link.name ? 'page' : undefined"
            role="menuitem"
            data-test="nav-link"
          >
            {{ link.text }}
          </router-link>

          <!-- Programs Dropdown -->
          <div class="relative" ref="programsDropdownRef">
            <button
              @click="isProgramsMenuOpen = !isProgramsMenuOpen"
              :class="[
                'nav-link flex items-center gap-1',
                isProgramsActive ? 'active' : ''
              ]"
              :aria-expanded="isProgramsMenuOpen"
              aria-haspopup="true"
              data-test="programs-dropdown"
            >
              Programs
              <svg class="w-4 h-4 transition-transform" :class="isProgramsMenuOpen ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>
            <transition name="dropdown">
              <div
                v-if="isProgramsMenuOpen"
                class="absolute left-0 mt-2 w-64 sm:w-72 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 py-2 z-50"
              >
                <router-link
                  v-for="prog in programLinks"
                  :key="prog.name"
                  :to="prog.path"
                  class="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  @click="isProgramsMenuOpen = false"
                >
                  <div class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" :class="prog.iconBg">
                    <svg class="w-4 h-4" :class="prog.iconColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="prog.icon"/>
                    </svg>
                  </div>
                  <div>
                    <div class="font-medium text-gray-900 dark:text-white text-sm">{{ prog.text }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ prog.description }}</div>
                  </div>
                </router-link>
              </div>
            </transition>
          </div>
          
          <!-- Dark mode toggle -->
          <button
            @click="toggleDarkMode"
            @keydown.enter="toggleDarkMode"
            @keydown.space.prevent="toggleDarkMode"
            class="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
            :aria-pressed="isDark"
            role="switch"
            data-test="dark-mode-toggle"
          >
            <svg 
              v-if="!isDark" 
              class="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
            <svg 
              v-else 
              class="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </button>
          
          <!-- Auth Links -->
          <div v-if="!authStore.isAuthenticated" class="flex items-center space-x-4">
            <router-link to="/login" class="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium">
              Sign In
            </router-link>
            <router-link to="/signup" class="btn btn-primary">
              Get Started
            </router-link>
          </div>
          
          <!-- User Menu -->
          <div v-else class="relative">
            <button
              @click="isUserMenuOpen = !isUserMenuOpen"
              class="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              :aria-expanded="isUserMenuOpen"
              aria-haspopup="true"
            >
              <div class="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-medium">
                {{ userInitial }}
              </div>
              <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <!-- Dropdown Menu -->
            <transition name="dropdown">
              <div 
                v-if="isUserMenuOpen"
                class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-2 z-50"
              >
                <router-link
                  to="/dashboard"
                  class="block px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  @click="isUserMenuOpen = false"
                >
                  Dashboard
                </router-link>
                <router-link
                  to="/profile"
                  class="block px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  @click="isUserMenuOpen = false"
                >
                  Profile
                </router-link>
                <hr class="my-2 border-gray-200 dark:border-gray-700">
                <button
                  @click="signOut"
                  class="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Sign Out
                </button>
              </div>
            </transition>
          </div>
        </div>

        <!-- Mobile menu button -->
        <div class="md:hidden">
          <button
            @click="toggleMobileMenu"
            @keydown.enter="toggleMobileMenu"
            @keydown.space.prevent="toggleMobileMenu"
            class="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            :aria-label="isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'"
            :aria-expanded="isMobileMenuOpen"
            aria-controls="mobile-menu"
            data-test="mobile-menu-toggle"
          >
            <svg 
              v-if="!isMobileMenuOpen" 
              class="w-6 h-6" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg 
              v-else 
              class="w-6 h-6" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile Navigation -->
      <transition name="slide-down">
        <div 
          v-if="isMobileMenuOpen" 
          id="mobile-menu"
          class="md:hidden py-4 border-t border-gray-200 dark:border-gray-800"
          role="menu"
          aria-label="Mobile navigation menu"
        >
          <div class="flex flex-col space-y-2">
            <router-link
              v-for="link in navLinks"
              :key="link.name"
              :to="link.path"
              :class="[
                'mobile-nav-link',
                route.name === link.name ? 'active' : ''
              ]"
              :aria-current="route.name === link.name ? 'page' : undefined"
              role="menuitem"
              @click="closeMobileMenu"
              @keydown.enter="closeMobileMenu"
            >
              {{ link.text }}
            </router-link>

            <!-- Programs group in mobile -->
            <div class="px-4 pt-2 pb-1">
              <div class="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">Programs</div>
              <router-link
                v-for="prog in programLinks"
                :key="prog.name"
                :to="prog.path"
                :class="[
                  'mobile-nav-link flex items-center gap-2 mb-1',
                  route.name === prog.name ? 'active' : ''
                ]"
                @click="closeMobileMenu"
              >
                <div class="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" :class="prog.iconBg">
                  <svg class="w-3.5 h-3.5" :class="prog.iconColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="prog.icon"/>
                  </svg>
                </div>
                {{ prog.text }}
              </router-link>
            </div>
            
            <button
              @click="toggleDarkMode"
              @keydown.enter="toggleDarkMode"
              @keydown.space.prevent="toggleDarkMode"
              class="mobile-nav-link flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-50 dark:focus:ring-offset-gray-800"
              :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
              :aria-pressed="isDark"
              role="switch"
            >
              <span>{{ isDark ? 'Light Mode' : 'Dark Mode' }}</span>
              <svg 
                v-if="!isDark" 
                class="w-5 h-5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
              <svg 
                v-else 
                class="w-5 h-5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </button>
          </div>
        </div>
      </transition>
    </div>
  </nav>
</template>

<script setup>
import { ref, onMounted, computed, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const isMobileMenuOpen = ref(false)
const isUserMenuOpen = ref(false)
const isProgramsMenuOpen = ref(false)
const programsDropdownRef = ref(null)
const isDark = ref(false)

// Navigation links — audience-first, no How It Works (absorbed into For Talent)
const navLinks = [
  { name: 'for-talent', path: '/for-talent', text: 'For Talent' },
  { name: 'for-employers', path: '/for-employers', text: 'For Employers' },
  { name: 'pledge', path: '/pledge', text: 'Pledge' },
  { name: 'for-mentors', path: '/for-mentors', text: 'For Mentors' },
  { name: 'about', path: '/about', text: 'About' },
  { name: 'contact', path: '/contact', text: 'Contact' }
]

// Programs dropdown links
const programLinks = [
  {
    name: 'programs',
    path: '/programs',
    text: 'All Programs',
    description: 'Find your path — compare all three tracks',
    icon: 'M4 6h16M4 10h16M4 14h16M4 18h16',
    iconBg: 'bg-indigo-100 dark:bg-indigo-900/30',
    iconColor: 'text-indigo-600 dark:text-indigo-400'
  },
  {
    name: 'ai-skills-academy',
    path: '/ai-skills-academy',
    text: 'AI Skills Academy',
    description: '6-week remote AI-First development course',
    icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400'
  },
  {
    name: 'ai-incubator',
    path: '/ai-incubator',
    text: 'AI Incubator',
    description: 'Mentored AI-First entrepreneurship program',
    icon: 'M13 10V3L4 14h7v7l9-11h-7z',
    iconBg: 'bg-orange-100 dark:bg-orange-900/30',
    iconColor: 'text-orange-600 dark:text-orange-400'
  },
  {
    name: 'ai-upskilling',
    path: '/ai-upskilling',
    text: 'AI Upskilling Curriculum',
    description: 'Pathways for talent, mentors & employers',
    icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
    iconBg: 'bg-purple-100 dark:bg-purple-900/30',
    iconColor: 'text-purple-600 dark:text-purple-400'
  }
]

// Whether any program route is currently active
const isProgramsActive = computed(() =>
  programLinks.some(p => route.name === p.name) || route.name === 'programs'
)

// Computed properties
const userInitial = computed(() => {
  if (authStore.userProfile?.full_name) {
    return authStore.userProfile.full_name.charAt(0).toUpperCase()
  }
  if (authStore.user?.email) {
    return authStore.user.email.charAt(0).toUpperCase()
  }
  return 'U'
})

// Mobile menu functions
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
}

// Close Programs dropdown when clicking outside
const handleClickOutside = (event) => {
  if (programsDropdownRef.value && !programsDropdownRef.value.contains(event.target)) {
    isProgramsMenuOpen.value = false
  }
}

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Dark mode toggle
const toggleDarkMode = () => {
  isDark.value = !isDark.value
  if (isDark.value) {
    document.documentElement.classList.add('dark')
    localStorage.setItem('darkMode', 'true')
  } else {
    document.documentElement.classList.remove('dark')
    localStorage.setItem('darkMode', 'false')
  }
}

// Sign out
async function signOut() {
  isUserMenuOpen.value = false
  await authStore.signOut()
  router.push('/')
}

// Check for saved dark mode preference
onMounted(() => {
  const savedDarkMode = localStorage.getItem('darkMode')
  if (savedDarkMode === 'true' || (!savedDarkMode && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    isDark.value = true
    document.documentElement.classList.add('dark')
  }
  document.addEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.nav-link {
  @apply text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium transition-colors;
}

.nav-link.active {
  @apply text-primary-600 dark:text-primary-400;
}

.mobile-nav-link {
  @apply px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-medium transition-colors;
}

.mobile-nav-link.active {
  @apply bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-down-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Dropdown animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  transform: translateY(-5px);
  opacity: 0;
}
</style>